import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {Accordion, Button, Card, Overlay, Table, Tooltip} from 'react-bootstrap'

import { RootState } from '../../store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isSmallMobileQuery, formatPrice } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import { IRegistration } from '../../store/registrations/interfaces'
import { calculateCustomPrice } from '../../store/registrations/registrations'
import { filterExistingRegistrations, filterNewRegistrations } from '../../store/registrations/selectors'


const PaymentDetails = (props: {
  registrations: IRegistration[],
  isVerifyStage?: boolean
}) => {
  const {registrations, isVerifyStage} = props
  const {
    basic_fee,
    payment_configuration: { smallprint },
  } = useSelector((state: RootState) => state.program.data)

  const {
    discountCodes,
  } = useSelector((state: RootState) => state.paymentData)

  const {
    data: program,
  } = useSelector((state: RootState) => state.program)

  const [show, setShow] = useState(false)
  const [existingOpen, setExistingOpen] = useState(false)
  const [regs, setRegs] = useState(registrations);
  const target = useRef<HTMLSpanElement>(null)

  const isSmallMobile = useMediaQuery(isSmallMobileQuery)

  useEffect(() => {
    setRegs(regs.map((r) => {
      const customPrice = calculateCustomPrice(r, discountCodes, program)
      return {...r, customPrice: customPrice >= 0 ? customPrice : -1}
    }))
  },[discountCodes.length])

  const newRegs = filterNewRegistrations(regs)
  const existingRegs = filterExistingRegistrations(regs)

  const regPrice = (reg: IRegistration) => (reg.customPrice >= 0 ? reg.customPrice : reg.price)

  // Paid to the institution via the selected payment method
  const institutionRegs = newRegs.filter((reg: IRegistration) => !reg.event?.has_external_payment)
  const totalPrice = institutionRegs.reduce((sum: number, eReg: IRegistration) => sum + regPrice(eReg), basic_fee)

  // Paid directly to the respective organizer
  const externalRegs = newRegs.filter((reg: IRegistration) => reg.event?.has_external_payment)
  const externalPrice = externalRegs.reduce((sum: number, eReg: IRegistration) => sum + regPrice(eReg), 0)

  const renderRegRow = (reg: IRegistration) => (
    <tr key={`payment-reg-${reg.event_id}`}>
      <td>
        {reg.event_name} (#{reg.event_rid})
      </td>
      <td className={reg.price === 0 ? 'text-muted pull-right' : 'pull-right'}>
        {reg.customPrice >= 0 ? <p>{formatPrice(reg.customPrice)} <s className="text-danger">{formatPrice(reg.price)}</s></p> : <p>{formatPrice(reg.price)}</p>}
      </td>
    </tr>
  )

  return (
    <>
      <Table responsive>
        <tbody>
        <tr>
          <th>Zahlung an die Institution</th>
        </tr>
        {institutionRegs.map(renderRegRow)}
        {basic_fee > 0 && (
          <tr>
            <td>
              Grundgebühr{' '}
              <small>
                  <span
                    className='badge bg-dark text-white'
                    ref={target}
                    onClick={() => setShow(!show)}
                  >
                    <FontAwesomeIcon icon='question' />
                  </span>
                {target && target.current && (
                  <Overlay
                    target={target.current}
                    show={show}
                    placement={isSmallMobile ? 'bottom' : 'right'}
                  >
                    {(props: any) => (
                      <Tooltip {...props}>
                        Die Grundgebühr fällt nur einmal pro Teilnehmer an,
                        wenn mindestens eine Anmeldung akzeptiert wird.
                      </Tooltip>
                    )}
                  </Overlay>
                )}
              </small>
            </td>
            <td className='pull-right'>{formatPrice(basic_fee)}</td>
          </tr>
        )}
        <tr className='payment-total'>
          <td>
            <strong>Summe</strong>
          </td>
          <td className='pull-right'>
            <strong>{(totalPrice).toFixed(2)}€</strong>
          </td>
        </tr>
        </tbody>
      </Table>
      {externalRegs.length > 0 && (
        <Table responsive>
          <tbody>
          <tr>
            <th>Zahlung direkt beim Veranstalter</th>
          </tr>
          {externalRegs.map(renderRegRow)}
          <tr className='payment-total'>
            <td>
              <strong>Summe</strong>&nbsp;
              <small className='text-muted'>
                nicht bei der Institution zu bezahlen
              </small>
            </td>
            <td className='pull-right'>
              <strong>{formatPrice(externalPrice)}</strong>
            </td>
          </tr>
          </tbody>
        </Table>
      )}
      {existingRegs.length > 0 && (
        <Accordion className='mb-3' activeKey={existingOpen ? '0' : undefined}>
          <Card>
            <Card.Header>
              {/* Keep the header padding so the button's focus ring is not clipped by
                  the accordion card's overflow: hidden */}
              <Button
                variant='link'
                onClick={() => setExistingOpen(!existingOpen)}
                aria-expanded={existingOpen}
                aria-controls='payment-existing-registrations'
                className='text-left text-reset p-0'
                block
              >
                <FontAwesomeIcon icon={existingOpen ? 'caret-down' : 'caret-right'} />{' '}
                Bestehende Anmeldungen ({existingRegs.length})
              </Button>
            </Card.Header>
            <Accordion.Collapse eventKey='0' id='payment-existing-registrations'>
              <Card.Body>
                <ul className='mb-0'>
                  {existingRegs.map((reg: IRegistration) => (
                    <li key={`payment-existing-${reg.event_id}`}>
                      {reg.event_name} (#{reg.event_rid})
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      )}
      <small className='text-muted'>
        <strong>Bitte beachten: </strong>
        Die aufgelisteten Beträge sind vorläufig und gelten nur für den Fall,
        dass jede Anmeldung auch zugeteilt wird. Die letztendlich zu zahlenden
        Kosten stehen erst nach erfolgter Zuteilung fest.
      </small>
      {smallprint && (
        <div className='mt-3'>
          <strong>Hinweise</strong>
          <div dangerouslySetInnerHTML={{ __html: smallprint }} />
        </div>
      )}
      {discountCodes && discountCodes.length > 0 && isVerifyStage && (
        <>
          <hr/>
          <div className='mt-3'>
            <strong>Angewendete Rabattcodes:</strong>
            <ul>
              {discountCodes.map(code => (
                <li key={code.code}>{code.code}</li>
              ))}
            </ul>
          </div>
        </>
      )}
    </>
  )
}

export default PaymentDetails
